import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { UserProfile1 } from '../types';
import { Button } from '../components_catalyst/button';
import { Field, Label } from '../components_catalyst/fieldset';
import { Input } from '../components_catalyst/input';
import { Text } from '../components_catalyst/text';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { debounce } from 'lodash';
import { getAuth, signOut } from 'firebase/auth';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import {
  createUserProfile,
  updateSignUpLink,
  processOperationQueue,
} from '../store/map3PendingSlice';
import { SaveAndRefresh } from '../components_misc/SaveAndRefresh';

export function CompleteSignup() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const dispatch = useDispatch<AppDispatch>();
  const signUpLink = useSelector((state: RootState) => state.map3Data.signUpLink);
  const reduxError = useSelector((state: RootState) => state.map3Data.error);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isCheckingUserName, setIsCheckingUserName] = useState(false);
  const [isUserNameValid, setIsUserNameValid] = useState<boolean | null>(null);

  const [formData, setFormData] = useState<Partial<UserProfile1>>({
    uid: user?.uid || '',
    userName: '',
    firstName: '',
    lastName: '',
    email: user?.email ? [user.email] : [],
    phone: user?.phoneNumber ? [user.phoneNumber] : [],
    accountType: 'alpha_pending', // Default, may be overridden by signup link
  });

  // Update error state when Redux error changes
  useEffect(() => {
    if (reduxError) {
      setError(reduxError);
    }
  }, [reduxError]);

  // Update form data when signup link changes
  useEffect(() => {
    if (signUpLink) {
      setFormData((prev) => ({
        ...prev,
        accountType: signUpLink.accountType,
      }));
    }
  }, [signUpLink]);

  // Username validation regex: letters, numbers, and dashes only, 4+ chars
  const userNameRegex = /^[a-zA-Z0-9-]{4,}$/;

  const checkUserName = async (userName: string): Promise<boolean> => {
    if (!userName || !userNameRegex.test(userName)) {
      return false;
    }

    const q = query(collection(db, 'userProfiles'), where('userName', '==', userName));
    const querySnapshot = await getDocs(q);
    return querySnapshot.empty;
  };

  // Debounced version of username check
  const debouncedCheckUserName = debounce(async (userName: string) => {
    setIsCheckingUserName(true);
    try {
      const isValid = await checkUserName(userName);
      setIsUserNameValid(isValid);
    } catch (error) {
      console.error('Error checking username:', error);
      setIsUserNameValid(false);
    } finally {
      setIsCheckingUserName(false);
    }
  }, 500);

  const handleUserNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUserName = e.target.value;
    setFormData({ ...formData, userName: newUserName });

    if (newUserName) {
      debouncedCheckUserName(newUserName);
    } else {
      setIsUserNameValid(null);
    }
  };

  useEffect(() => {
    // If display name exists, try to split it into first and last name
    if (user?.displayName) {
      const [firstName = '', lastName = ''] = user.displayName.split(' ');
      setFormData((prev) => ({
        ...prev,
        firstName,
        lastName,
      }));
    }
  }, [user]);

  const validateUserName = (userName: string): string | null => {
    if (!userName) {
      return 'Username is required';
    }
    if (!userNameRegex.test(userName)) {
      return 'Username must be at least 4 characters and contain only letters, numbers, and dashes';
    }
    if (!isUserNameValid) {
      return 'Username is already taken';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    console.log('CompleteSignup - Starting submission with:', {
      formData,
      signUpCode: signUpLink?.signUpCode,
      signUpLink,
    });

    const userNameError = validateUserName(formData.userName || '');
    if (userNameError) {
      setError(userNameError);
      setIsLoading(false);
      return;
    }

    // Only validate email match if there's a signup link
    if (signUpLink && user?.email !== signUpLink.toEmail) {
      setError('This signup code was created for a different email address');
      setIsLoading(false);
      return;
    }

    try {
      // Ensure we have the correct account type
      const userData = {
        ...formData,
        accountType: signUpLink?.accountType || 'alpha_pending',
      } as const;

      // First create the user profile
      await dispatch(createUserProfile({ userData })).unwrap();

      // Then update the signup link status if we're using one
      if (signUpLink?.signUpCode) {
        await dispatch(
          updateSignUpLink({
            signUpCode: signUpLink.signUpCode,
            updates: {
              status: 'accepted',
              acceptedDate: new Date().toISOString(),
              attempts: (signUpLink.attempts || 0) + 1,
              lastAttempt: new Date().toISOString(),
            },
          }),
        ).unwrap();
      }

      // Wait for the operation queue to process.
      // Process the queue to ensure the user profile is created and the signup link
      // is updated before signing out.
      await dispatch(processOperationQueue()).unwrap();

      // Now it's safe to sign out
      await signOut(getAuth());
      navigate('/sign-in?signUpComplete=true', { replace: true });
    } catch (err) {
      console.error('CompleteSignup - Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to save user data');
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-sm space-y-8">
      <SaveAndRefresh hidden />

      <header>
        <h2 className="text-2xl font-bold text-center" id="complete-signup-title">
          Complete Your Profile
        </h2>
        <p className="text-gray-500 text-center mt-2">
          Please provide some additional information to complete your signup.
        </p>
      </header>

      <main aria-labelledby="complete-signup-title">
        {error && (
          <Text className="text-red-600 text-center" role="alert">
            {error}
          </Text>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" aria-label="Complete signup form">
          <Field>
            <Label htmlFor="username-input">Username *</Label>
            <div className="relative">
              <Input
                id="username-input"
                value={formData.userName || ''}
                onChange={handleUserNameChange}
                className={`pl-10 ${
                  isUserNameValid === true
                    ? 'border-green-500'
                    : isUserNameValid === false
                      ? 'border-red-500'
                      : ''
                }`}
                required
                aria-required="true"
                placeholder="Choose a username"
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                {isCheckingUserName ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                ) : isUserNameValid === true ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                ) : isUserNameValid === false ? (
                  <XCircleIcon className="h-5 w-5 text-red-500" />
                ) : null}
              </div>
            </div>
            <Text className="text-sm text-gray-500 mt-1">
              Must be at least 4 characters, using only letters, numbers, and dashes.
            </Text>
          </Field>

          <Field>
            <Label htmlFor="firstname-input">First name</Label>
            <Input
              id="firstname-input"
              value={formData.firstName || ''}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              aria-required="false"
              placeholder="Enter your first name"
            />
          </Field>

          <Field>
            <Label htmlFor="lastname-input">Last name</Label>
            <Input
              id="lastname-input"
              value={formData.lastName || ''}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              aria-required="false"
              placeholder="Enter your last name"
            />
          </Field>

          <Field>
            <Label>Email *</Label>
            <Input
              type="email"
              value={formData.email?.[0] || ''}
              onChange={(e) => setFormData({ ...formData, email: [e.target.value] })}
              disabled={!!signUpLink} // Disable if using signup link
              required
              aria-required="true"
            />
          </Field>

          <Field>
            <Label>Phone</Label>
            <Input
              type="tel"
              value={formData.phone?.[0] || ''}
              onChange={(e) => setFormData({ ...formData, phone: [e.target.value] })}
              aria-required="false"
              placeholder="Enter your phone number"
            />
          </Field>

          <Text className="text-sm text-gray-500 mb-4">Fields marked with * are required</Text>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full"
            aria-label="Complete signup"
          >
            {isLoading ? 'Saving...' : 'Complete signup'}
          </Button>
        </form>
      </main>
    </div>
  );
}
